/* eslint-disable max-classes-per-file*/

import { JQueryHelper } from './jquery-helper';

interface Closeable {
	open: () => void;
	close: () => void;
}

/**
 * focusin event is supposed to contain the previous element which had focus, but could not get it to work so
 */
export class FocusHandler {
	private static readonly focusQueue = new Array<Focusable>();

	public static restore() {
		const obj = FocusHandler.focusQueue[FocusHandler.focusQueue.length - 1];
		if (obj) {
			$('#' + obj.focusableBlockId).focus();
		}
	}

	public static open(obj: Focusable) {
		FocusHandler.focusQueue.push(obj);
		JQueryHelper.whenReady('#' + obj.focusableBlockId).then((o) =>
			o.focus(),
		);
	}

	/**
	 * @param originalObj If this is provided, the top element on the stack must be equal to originalObj
	 */
	public static close(originalObj: Focusable) {
		if (
			originalObj &&
			originalObj !==
				FocusHandler.focusQueue[FocusHandler.focusQueue.length - 1]
		) {
			return;
		}
		originalObj = FocusHandler.focusQueue.pop();
		// remove focus from closed object
		if (originalObj) {
			$('#' + originalObj.focusableBlockId).blur();
		}

		const obj = FocusHandler.focusQueue[FocusHandler.focusQueue.length - 1];
		if (obj) {
			JQueryHelper.whenReady('#' + obj.focusableBlockId).then((o) =>
				o.focus(),
			);
		}
	}
}

type Focusable = Closeable & {
	focusableBlockId: string;
	attached: () => void;
	prototype: any;
};

export enum ElementType {
	TAG,
	ID,
	CLASS,
}

export default class FocusableElement {
	public value: string;
	// tslint:disable-next-line:no-reserved-keywords
	public type: ElementType;
}

/**
 * Focuses the element on open, returns focus on close
 *
 * Must be before 'autoinject'
 */
export function focusable(
	focusableElement?: FocusableElement | { prototype: any },
): any {
	if (focusableElement && focusableElement.hasOwnProperty('prototype')) {
		// no settings provided, this is the constructor
		applyFocusSettings(focusableElement);
		return tryToRetrieveFocusableElement(<any>focusableElement);
	} else {
		return (constrctr: any) => {
			applyFocusSettings(constrctr);
			return constrctr;
		};
	}

	function applyFocusSettings(constrctr: any) {
		const originalOpen = constrctr.prototype.open;
		constrctr.prototype.open = function (...args) {
			FocusHandler.open(this);
			originalOpen.apply(this, args);
		};

		const originalClose = constrctr.prototype.close;
		constrctr.prototype.close = function (...args) {
			FocusHandler.close(this);
			originalClose.apply(this, args);
		};
	}

	/**
	 * Injects ContentWithKeybindings on form load.
	 *
	 * If ContentWithKeybindings tag is present, uses id from this.
	 * @param constrctr
	 * @returns {Function}
	 */
	function tryToRetrieveFocusableElement<
		T extends { new (...args: any[]); inject: object },
	>(constrctr: T) {
		return class extends constrctr {
			public static inject = [Element];
			public element;
			public focusableBlockId;

			constructor(...args) {
				const element = args.shift();
				super(...args);
				this.element = element;
			}

			public attached(...args) {
				if (!this.focusableBlockId) {
					let selector = this.element.getId
						? '#' + $(this.element.getId())
						: this.element.tagName;
					selector +=
						' content-with-keybindings .content-with-keybindings';
					this.focusableBlockId = $(selector).attr('id');
				}
				if (super.attached) {
					super.attached(...args);
				}
			}
		};
	}
}
